/**
 * Centralized content strings for the entire application
 * All text content (titles, descriptions, button text) should be referenced from here
 * Organized by route/page for easy navigation and maintenance
 */

export const content = {
	marketing: {
		home: {
			hero: {
				// title: "The Last<br /> Rental Application <br />You'll Ever Fill Out",
				title: 'Your Verified <br />Rental Profile for Canada',
				buttonText: 'Start Screening',
			},
			sections: {
				howItWorks: {
					title: 'How TrueTenant Works?',
					subTitle: 'A simple, secure way to verify your rental profile',
				},
				whyTrueTenant: {
					title: 'What sets TrueTenant apart?',
					subTitle: 'Renting should be faster, simpler, & stress-free.',
				},
				findHouse: {
					title: 'Search Smarter. Rent Better.',
					description: 'Find listings that match your profile and budget.',
					houses: [
						{
							title: '789 River Road, Apt 5C',
							location: 'Riverside District',
						},
						{
							title: '789 River Road, Apt 5C',
							location: 'Riverside District',
						},
						{
							title: '789 River Road, Apt 5C',
							location: 'Riverside District',
						},
						{
							title: '789 River Road, Apt 5C',
							location: 'Riverside District',
						},
						{
							title: '789 River Road, Apt 5C',
							location: 'Riverside District',
						},
					],
				},
			},
		},
		landlord: {
			hero: {
				title:
					'The Landlord Leasing Partner for Tenant Background & Credit Checks',
				buttonText: 'Start Free Trial',
			},
			sections: {
				howItWorks: {
					title: 'How It Works',
					steps: [
						{
							title: 'Add Your Property',
							desc: 'List your rental in minutes. Add property details once and start processing applications instantly with our efficient tenant screening workflow.',
						},
						{
							title: 'Receive Applications',
							desc: 'Each property generates a unique QR code or link, allowing tenants to apply instantly and simplifying the application process for landlords',
						},
						{
							title: 'AI Reviews & Ranks',
							desc: 'TrueTenant uses AI to analyze thousands of data points across income, identity, rental history, and credit signals, instantly generating clear, data-driven tenant risk assessments.',
						},
						{
							title: 'Make Your Decision',
							desc: 'Approve tenants confidently with data-driven screening reports. Go beyond credit checks by analyzing background, income, and risk signals through one secure tenant screening service.',
						},
					],
				},
				platformSection: {
					title:
						'More Than A Tenant Screening Service: We Are an End-to-End Tenant Management System',
					togglebuttons: [
						{ label: 'Tenant Screening' },
						{ label: '24/7 Voice Leasing Agent' },
						{ label: 'Rent Guarantee' },
						{ label: 'AI Lease Generator' },
						{ label: 'Rental Payments' },
						{ label: 'Marketplace' },
					],
				},
				whyTrueTenant: {
					title: 'Security You Can Count On',
					features: [
						{
							title: 'Modern Security Architecture',
							desc: 'Enterprise-grade encryption trusted by leading financial institutions. Share information securely, only when you choose.',
						},
						{
							title: 'Verified Financial Data',
							desc: 'Powered by Flinks, tenant income and employment are verified at the source, eliminating document fraud and giving landlords confidence in every decision.',
						},
						{
							title: 'Fair & Compliant',
							desc: 'TrueTenant is built to align with Canadian rental laws and compliance standards. Our fair, unbiased tenant screening service protects both landlords and renters.',
						},
					],
				},
				leasingTeamMemberSection: {
					title: 'Your 24/7 Leasing Assistant',
					features: [
						'Never miss a lead or an opportunity.',
						'Automatically schedule property showings',
						'Instant recap emails with key details and documents',
						'Automated follow-ups that keep deals moving',
					],
				},
				differenceSection: {
					heading: 'The Difference Is Clear',

					trueTenant: {
						title: 'The Modern Way of Tenant Screening',
						features: [
							'Screen tenants in just 5 minutes',
							'Flat $35 per applicant with no hidden fees',
							'Use data-driven, verified sources to screen tenants and reduce risk.',
							'All your tenant data in one place',
						],
					},

					traditional: {
						title: 'The Old Way of Screening Tenants ',
						features: [
							'Takes 2+ hours per applicant with manual work',
							'$60+ in time and administrative costs',
							'Inconsistent, subjective decisions',
							'Manual reviews increase the risk of missed red flags',
						],
					},
				},
				resultsStatsSection: {
					heading: 'Real Results From<br/>Real Landlords',
					stats: {
						vacancy: {
							value: 40,
							suffix: '%',
							text: 'faster vacancy<br/>fill rate',
						},
						screening: {
							value: 95,
							suffix: '%',
							text: 'reduction in<br/>screening time',
						},
						savings: {
							value: 850,
							prefix: '$',
							text: 'saved per<br/>10 applicants',
						},
					},
				},
				pricingSection: {
					heading: 'Simple, Transparent Pricing',
					subheading:
						'Choose the plan that fits your portfolio. No hidden fees. No long-term contracts.',

					billingToggle: {
						monthly: 'Monthly',
						yearly: 'Yearly',
						discountLabel: '30% Off',
					},

					plans: [
						{
							title: 'Starter',
							suffix: '/ tenant screen',
							bestFor: 'Individual landlords with 1–10 units',
							cta: 'Screen a Tenant',
							included: [
								'Tenant Credit Report',
								'Income & Employment Verification',
								'Eviction History Check',
								'TrueTenant Report',
							],
							excluded: [
								'Advanced AI risk scoring',
								'Rent collection & guarantee',
								'Marketplace priority listing',
							],
						},
						{
							title: 'Professional',
							suffix: ': 10 Screens',
							bestFor: 'Landlords & property managers with 10–50 units',
							cta: 'Book a Call',
							included: [
								'Advanced AI risk scoring ',
								'Priority marketplace listing ',
								'Team access (up to 3 users) ',
								'Rank your prospective tenants',
							],
							excluded: [
								'Rent guarantee',
								'Dedicated account manager',
								'Custom integrations',
							],
						},
						{
							title: 'Enterprise',
							suffix: '· Built for Scale',
							bestFor: 'Portfolio owners managing 50+ units',
							cta: 'Book a Call',
							included: [
								'Enterprise-grade screening and leasing, customized to your scale. Tailored pricing and features designed for high-volume portfolios. Get in touch now!',
							],
							excluded: [
								'Rent guarantee',
								'Dedicated account manager',
								'Custom integrations',
							],
						},
					],
				},
				ctaSection: {
					title: 'Still Have Questions?',
					buttonText: 'Contact Us',
				},
				faqSection: {
					title: 'Your Questions Answered',
					subtitle: 'Here are some most common questions',
					faq: [
						{
							title: 'How to do a credit check on a tenant?',
							content:
								'Landlords can run a tenant credit check using a tenant screening service or directly through a Canadian credit bureau such as Equifax or TransUnion.',
						},
						{
							title: 'Can a tenant with bad credit still rent in Canada?',
							content:
								'Yes, a tenant with poor credit can still rent in Canada. Landlords might accept tenants with lower credit scores if they demonstrate a stable income.',
						},
						{
							title:
								'What questions should landlords ask before running tenant screening?',
							content:
								'Common pre-screening questions include move-in date, number of occupants, employment status, and prior rental history. These help determine basic eligibility before requesting formal screening.',
						},
						{
							title:
								'Why do Canadian landlords prefer online tenant screening platforms over manual checks?',
							content:
								'Online tenant screening platforms offer quicker and more accurate results than manual checks by utilizing verified data and standardized reports. They help landlords reduce fraud, avoid bias, and make informed decisions, eliminating the time and errors associated with manual processes.',
						},
						{
							title: 'How does TrueTenant use Flinks to verify tenant income?',
							content:
								'TrueTenant uses Flinks to let tenants share verified income instantly and securely. Landlords see only a verified summary, not bank transactions, making income verification fast, safe, and privacy-first.',
						},
						{
							title: 'How does TrueTenant use Flinks to verify tenant income? ',
							content:
								'TrueTenant uses Flinks to let tenants share verified income instantly and securely. The bank connection is read-only, encrypted, with no credentials stored. Landlords see only a verified summary, not bank transactions, making income verification fast, safe, and privacy-first.',
						},
					],
				},
			},
		},
		tenant: {
			hero: {
				title: "The Last, Rental Application You'll Ever Fill Out",
				buttonText: 'Start Screening',
			},
			sections: {
				howItWorks: {
					title: 'How It Works',
					steps: [
						{
							title: 'Secure Income Verification',
							desc: 'Verify your income safely by connecting your bank account through a secure connection. No screenshots. No PDFs. No manual checks.',
						},
						{
							title: 'Identity Verification',
							desc: 'Confirm your identity by using a government issued ID and a quick biometric scan.',
						},
						{
							title: 'Employment & Rental History',
							desc: 'Employment and rental history are collected to ensure that all necessary details are included in the application.',
						},
						{
							title: 'Your TrueTenant Score',
							desc: 'Get your score after completing the one-time application and stand out from other applicants. Apply now and get approved instantly!',
						},
					],
				},
				whyTrueTenant: {
					title: 'What sets TrueTenant apart?',
					subTitle: 'Renting should be faster, simpler, & stress-free.',
					features: [
						{
							title: 'Verified In Minutes',
							desc: 'Verify income, answer a few questions about your rental and employment history, and complete your application in under 5 minutes.',
						},
						{
							title: 'All-in-One Rental Profile',
							desc: 'One application gives you the freedom to apply to more listings with less effort, helping you move faster and stay competitive in a busy rental market.',
						},
						{
							title: 'Unlock More Listings',
							desc: `Having a TrueTenant profile enhances a tenant's trustworthiness, making them more eligible for a wider range of properties and increasing their chances of securing their desired rental.`,
						},
					],
				},
				tenentScore: {
					title: 'Two Trusted Platforms. One Smarter Screening Experience.',
					description:
						'By collaborating with Flinks, TrueTenant enables income verification in seconds. The process is fully encrypted, private, and eliminates the need for manual screening.',
				},
				featureSection: {
					heading: 'Everything You Need <br />In One Place',
					leftFeature: [
						{
							title: 'Apply Instantly',
							desc: 'Apply using your verified rental profile without filling out forms again. Your one-time tenant verification creates a shareable tenant profile that landlords can review instantly.',
						},
						{
							title: 'Pay Rent',
							desc: 'Set up secure monthly payments and view your complete payment history at any time. Your renter verification stays connected to your rental profile, keeping everything organized in one place.',
						},
						{
							title: 'Sign Leases',
							desc: 'Review and sign lease agreements digitally with secure encryption measures. Your verified renter profile supports secure tenant screening and faster move-ins.',
						},
						{
							title: 'Track Applications',
							desc: 'Get real-time updates on every rental application you submit. Your rental application profile helps you track progress and get approved for rent fast without follow-ups.',
						},
					],
				},
				secureAccessSection: {
					title: 'Rent Without Re-Entering Everything',
					description: 'One Setup for Every Rental Application',
				},
				testimonialsData: {
					title: 'Trusted by Renters Across Canada',
					description: 'Honest experiences from people who uses TrueTenant',
					testimonials: [
						{
							text: 'TrueTenant Rentals made finding an apartment in Toronto so much less stressful. Their eligibility check saved me so much time!',
							name: 'Mark T.',
							location: 'Vancouver, BC',
						},
						{
							text: 'As a landlord, screening tenants used to be a headache. TrueTenant provides qualified applicants, making my job so much easier.',
							name: 'Sarah L.',
							location: 'Toronto, ON',
						},
						{
							text: 'The process is smooth and transparent. I finally feel confident renting with peace of mind.',
							name: 'Emily R.',
							location: 'Calgary, AB',
						},
						{
							text: 'TrueTenant made everything simple. I found my perfect apartment in no time.',
							name: 'Alex P.',
							location: 'Ottawa, ON',
						},
					],
				},
				faqSection: {
					title: 'Your Questions Answered',
					subtitle: 'Here are some most common questions',
					faq: [
						{
							title: 'What Documents Are Needed for Tenant Verification?',
							content:
								'To get verified, renters simply provide a valid government-issued ID and securely connect their bank account. This enables identity and income verification, creating a trusted and complete rental application profile.',
						},
						{
							title: 'How Do I Know Which Rentals I’m Eligible For?',
							content:
								'Your verified renter profile and TrueTenant Score help match you with rentals that align with your income, rental history, and verification status. This allows you to apply confidently without submitting multiple applications blindly.',
						},
						{
							title: 'Do I Need to Upload Documents More Than Once?',
							content:
								'No. TrueTenant offers one-time tenant verification. Once your information is verified, you can reuse your renter profile for future rental applications without needing to re-upload documents.',
						},
						{
							title: 'How Long Does Tenant Verification Take?',
							content:
								'Most renters complete the tenant verification process in just a few minutes. After verification, your shareable renter profile is available for immediate use when applying for rentals. ',
						},
						{
							title: 'How do landlords verify employment and income?',
							content:
								'Employment and income are commonly verified through pay stubs, employer references, or bank statements. TrueTenant uses Flinks to securely verify income directly from financial institutions, which is safer and more reliable than sharing bank statement images over email.',
						},
						{
							title: 'Can a tenant still qualify with a poor credit history? ',
							content:
								'Yes. Tenants with lower credit scores may still qualify by providing proof of stable income and adding a guarantor.  ',
						},
						{
							title:
								'What questions do landlords ask during the pre-screening process?',
							content:
								'Common pre-screening questions include expected move-in date, number of occupants, rental history, and employment status. These help landlords assess basic eligibility before running a full screening.',
						},
						{
							title:
								'Can a tenant dispute errors on a tenant screening report in Canada?',
							content:
								'Yes. If you believe your tenant screening report contains inaccurate information, you can dispute it directly with our support team. We’ll review the details and begin the appropriate correction process to ensure your report is accurate and up to date. ',
						},
						{
							title: 'How can I check my tenant screening report online? ',
							content:
								'You can view your tenant screening report anytime by logging into your profile dashboard. All reports are securely stored and accessible in one place throughout the application process. ',
						},
						{
							title: 'How does TrueTenant screen applicants? ',
							content:
								'TrueTenant screens applicants using a comprehensive background check that includes identity verification, income and employment validation, credit insights, and rental history. These checks are analyzed together to deliver a clear, fair, and data-driven screening report instantly. ',
						},
					],
				},
			},
		},
	},
	dashboard: {
		helpAndSupport: {
			title: 'Help & Support',
			supportTicketCategories: [
				'Property Management',
				'Tenant Screening',
				'Billing & Payments',
				'Technical Issue',
				'Account Settings',
				'Other',
			],
			landlordFaq: {
				title: 'Frequently Asked Questions',
				subtitle: 'Quick Answers to Common Questions',
				items: [
					{
						question: 'How do I add a new property?',
						answer:
							'Navigate to the Properties page from the sidebar and click "Add Property". Fill in your property details including address, unit information, and upload photos. Your property will be live and ready to receive applications once saved.',
					},
					{
						question: 'How do I handle late payments?',
						answer:
							'You can track rent payments from the Rent Collection section. If a payment is overdue, TrueTenant will send automated reminders to the tenant. You can also manually send a notice or contact the tenant directly through the platform.',
					},
					{
						question: 'How do I end or renew a lease?',
						answer:
							'Go to the Leases section, select the active lease, and choose either "Renew" or "End Lease". For renewals, you can update the terms and send for the tenant to review. For endings, follow the guided off-boarding flow.',
					},
					{
						question: 'How do I review tenant applications?',
						answer:
							'All applications appear in the "Applications Requiring Actions" section on your dashboard. Click "View" on any application card to see the full screening report, TrueTenant Score, and verification details before making a decision.',
					},
					{
						question: 'How does the TrueTenant Score work?',
						answer:
							'The TrueTenant Score is a comprehensive assessment (0-100) based on verified income, employment history, rental history, identity verification, and credit insights. A higher score indicates a lower-risk tenant.',
					},
					{
						question: 'Can I share property access with my team?',
						answer:
							'Yes. On the Professional or Enterprise plan, you can invite team members to manage properties. Go to Settings and look for team management options to add collaborators with specific permissions.',
					},
				],
			},
			tenantFaq: {
				title: 'Frequently Asked Questions',
				subtitle: 'Quick Answers to Common Questions',
				items: [
					{
						question: 'How do I apply for a rental property?',
						answer:
							'Once you have completed your TrueTenant verification, you can apply to any property by scanning the QR code or clicking the application link provided by the landlord. Your verified profile is shared automatically.',
					},
					{
						question: 'How do I check my application status?',
						answer:
							'Go to "Your Applications" from the sidebar to see all your submitted applications. Each application shows the current status (Pending, Under Review, Approved, or Rejected) with real-time updates.',
					},
					{
						question: 'How do I update my personal information?',
						answer:
							'Navigate to Settings from the sidebar and select the Profile tab. You can update your first name, last name, email, and phone number. Click "Save Changes" to apply your updates.',
					},
					{
						question: 'What is the TrueTenant Score?',
						answer:
							'The TrueTenant Score is a comprehensive assessment that combines your income verification, employment history, rental history, and identity verification into a single score (0-100) that landlords use to evaluate applications.',
					},
					{
						question: 'Do I need to verify my identity for every application?',
						answer:
							'No. TrueTenant uses one-time verification. Once your identity and income are verified, your profile can be reused for all future rental applications without repeating the process.',
					},
					{
						question: 'How do I contact support?',
						answer:
							'You can reach us via Live Chat (9am-6pm EST), email at support@truetenant.com, or by submitting a support ticket from the Help & Support page in your dashboard.',
					},
				],
			},
		},
	},
	shared: {
		navigation: {
			landlordRoute: [
				'/landlord',
				'/tenant-screening',
				'/rent-collection',
				'/lease-generator',
				'/analytics-and-reports',
				'/about',
				'/support',
			],
			marketing: [
				// {
				// 	href: '/how-it-works',
				// 	name: 'How It Works',
				// 	children: [],
				// },
				// {
				// 	href: '/marketplace',
				// 	name: 'Marketplace',
				// },
				// {
				// 	href: '/support',
				// 	name: 'Support',
				// 	// children: [
				// 	// 	{
				// 	// 		href: '/tenant-screening',
				// 	// 		name: 'Tenant Screening',
				// 	// 	},
				// 	// ],
				// },
			],

			landlord: [
				// {
				// 	href: '/landlord#solutions',
				// 	name: 'Solutions',
				// 	children: [
				// 		{ href: '/tenant-screening', name: 'Tenant Screening' },
				// 		{ href: '/rent-collection', name: 'Rent Collection' },
				// 		{ href: '/lease-generator', name: 'Lease Generator' },
				// 		{ href: '/analytics-and-reports', name: 'Analytics & Reports' },
				// 	],
				// },
				// {
				// 	href: '/landlord#marketplace',
				// 	name: 'Marketplace',
				// },
				// {
				// 	href: '/landlord#rentguarantee',
				// 	name: 'Rent Guarantee',
				// },
				// {
				// 	href: '/landlord#pricing',
				// 	name: 'Pricing',
				// },
				// {
				// 	href: '/landlord#company',
				// 	name: 'Company',
				// 	children: [
				// 		{ href: '/tenant-screening', name: 'Tenant Screening' },
				// 		{ href: '/rent-collection', name: 'Rent Collection' },
				// 		{ href: '/lease-generator', name: 'Lease Generator' },
				// 		{ href: '/analytics-and-reports', name: 'Analytics & Reports' },
				// 	],
				// },
			],
		},

		buttons: {
			login: 'Log In',
			signup: 'Get Started',
			landlord: 'Landlord',
			tenant: 'Tenant',
			exploreMarketplace: 'Explore Marketplace',
			startScreening: 'Start Screening',
			getStarted: 'Get Started',
			startFreeTrial: 'Start Free Trial',
		},
		footer: {
			hero: {
				title: 'Ready To Transform Your Rental Business?',
				description:
					'Join hundreds of landlords who’ve automated their workflow',
				buttonText: 'Start Free Trial',
			},

			logoAlt: 'True Tenant',

			newsletter: {
				title: 'Join Our Newsletter',
				description:
					'Sign up for our mailing list below and be the first to know about new updates. Don’t worry, we hate spam too.',
				placeholder: 'Janecasanova@gmail.com',
				buttonText: 'Submit',
			},
			sectionTwo: {
				title: 'Pages',
				pages: [
					'Home',
					'Solutions',
					'Marketplace',
					'Rent Guarantee',
					'Pricing',
					'Company',
				],
			},
			sectionThree: {
				title: 'Social Media',
				social: ['X', 'Instagram', 'LinkedIn'],
			},

			copyright: 'Copyright © 2026 TrueTenant. All Rights Reserved',

			legal: {
				terms: 'Terms & Conditions',
				privacy: 'Privacy Policy',
			},
		},
	},
} as const
